import { type ComponentProps, useId, useRef } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import Badge from '@atlaskit/badge';
import { IconButton } from '@atlaskit/button-v25-3/new';
import DownloadIcon from '@atlaskit/icon/core/download';
import PanelRightIcon from '@atlaskit/icon/core/panel-right';
import Lozenge from '@atlaskit/lozenge-next/new';
import { Text } from '@atlaskit/primitives-next';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import {
	surveyChoiceAppearances,
	surveyChoiceLabels,
	surveyResponses,
	surveyTotals,
} from '../../jsm-portal-playground-hr-data';
import { jsmPortalPlaygroundHrMessages as messages } from '../../jsm-portal-playground-hr-messages';
import { JsmPlaygroundHeading } from '../jsm-playground-heading';
import { JsmPlaygroundPersonAvatar } from '../jsm-playground-person-avatar';
import { JsmPlaygroundRedactedLine } from '../jsm-playground-redacted-line';
import { JsmPlaygroundSearchField } from '../jsm-playground-search-field';
import { JsmPlaygroundSidePanel } from '../jsm-playground-side-panel';

import { surveyRespondentAvatars } from './survey-respondent-avatars';
import { SurveyResponsePanel } from './survey-response-panel';
import {
	SurveyCell,
	SurveyHeadCell,
	SurveyRowAction,
	SurveyTable,
	SurveyTableBody,
	SurveyTableHead,
	SurveyTableRow,
} from './survey-table';

type IconButtonIcon = NonNullable<ComponentProps<typeof IconButton>['icon']>;

const OpenPanelIcon: IconButtonIcon = () => <PanelRightIcon label="" size="medium" />;
const ExportIcon: IconButtonIcon = () => <DownloadIcon label="" size="medium" />;

const noop = () => {};

export type SurveyResponsesProps = {
	openResponseId: string | null;
	/** The last response the panel showed. Kept after closing, so the panel keeps its content while
	 * it animates closed. */
	panelResponseId: string | null;
	onOpenResponseChange: (responseId: string | null) => void;
	/** A row whose action shows as if hovered, for the demo cursor. */
	revealedResponseId?: string | null;
};

/**
 * The Responses tab: every submitted response as a row, and a panel that opens one beside the
 * table.
 *
 * Only opening and closing responses is live. Search and export exist to make the preview read as
 * the product, so they are drawn inert.
 */
export const SurveyResponses = ({
	openResponseId,
	panelResponseId,
	onOpenResponseChange,
	revealedResponseId = null,
}: SurveyResponsesProps) => {
	const intl = useIntl();
	const panelId = useId();
	const regionRef = useRef<HTMLDivElement>(null);

	const panelResponse = surveyResponses.find((response) => response.id === panelResponseId);
	const isPanelOpen = openResponseId !== null;

	const toggleResponse = (responseId: string) =>
		onOpenResponseChange(openResponseId === responseId ? null : responseId);

	const closePanel = () => {
		onOpenResponseChange(null);
		regionRef.current
			?.querySelector<HTMLButtonElement>(`[data-response-id="${panelResponseId}"] button`)
			?.focus();
	};

	return (
		<div css={responsesStyles}>
			<div css={headingRowStyles}>
				<span css={headingStyles}>
					<JsmPlaygroundHeading level={3} size="small">
						{intl.formatMessage(messages.tabResponses)}
					</JsmPlaygroundHeading>
					<Badge>{surveyTotals.responses}</Badge>
				</span>
				<span
					css={toolbarStyles}
					aria-hidden="true"
					// @ts-expect-error -- inert is valid HTML but is missing from @compiled/react types
					inert=""
				>
					<div css={searchFieldStyles}>
						<JsmPlaygroundSearchField
							label={intl.formatMessage(messages.searchResponses)}
							placeholder={intl.formatMessage(messages.searchResponses)}
							tabIndex={-1}
						/>
					</div>
					<IconButton
						icon={ExportIcon}
						label={intl.formatMessage(messages.exportResponses)}
						onClick={noop}
						tabIndex={-1}
					/>
				</span>
			</div>

			<div css={regionStyles} ref={regionRef}>
				<div css={tableColumnStyles}>
					<SurveyTable caption={intl.formatMessage(messages.responsesTableCaption)}>
						<SurveyTableHead>
							<SurveyHeadCell width={245} isFirst>
								{intl.formatMessage(messages.columnRespondent)}
							</SurveyHeadCell>
							<SurveyHeadCell width={285}>
								{intl.formatMessage(messages.questionOne)}
							</SurveyHeadCell>
							<SurveyHeadCell width={365}>
								{intl.formatMessage(messages.questionTwo)}
							</SurveyHeadCell>
							<SurveyHeadCell>{intl.formatMessage(messages.columnSubmitted)}</SurveyHeadCell>
						</SurveyTableHead>
						<SurveyTableBody>
							{surveyResponses.map((response, index) => {
								const { answers, id, respondent, satisfaction } = response;
								const isOpen = id === openResponseId;

								return (
									<SurveyTableRow
										key={id}
										index={index}
										isSelected={isOpen}
										isActionRevealed={id === revealedResponseId}
									>
										<SurveyCell isFirst>
											<span css={respondentCellStyles}>
												<span css={personStyles}>
													<JsmPlaygroundPersonAvatar src={surveyRespondentAvatars[id]} />
													{answers ? (
														<span css={nameStyles}>{respondent}</span>
													) : (
														<>
															<VisuallyHidden>{respondent}</VisuallyHidden>
															<JsmPlaygroundRedactedLine width={96} />
														</>
													)}
												</span>
												{/* Only rows with readable answers open: opening a redacted row would put text
												 * in the panel that the row itself withholds. */}
												{answers && (
													<SurveyRowAction>
														<span data-response-id={id}>
															<IconButton
																appearance="subtle"
																aria-controls={panelId}
																aria-expanded={isOpen}
																icon={OpenPanelIcon}
																isSelected={isOpen}
																label={intl.formatMessage(
																	isOpen ? messages.closeResponse : messages.openResponse,
																	{ name: respondent },
																)}
																onClick={() => toggleResponse(id)}
																spacing="compact"
															/>
														</span>
													</SurveyRowAction>
												)}
											</span>
										</SurveyCell>
										<SurveyCell>
											<Lozenge appearance={surveyChoiceAppearances[satisfaction]}>
												{answers ? (
													intl.formatMessage(surveyChoiceLabels[satisfaction])
												) : (
													<JsmPlaygroundRedactedLine width={56} />
												)}
											</Lozenge>
										</SurveyCell>
										<SurveyCell>
											{answers ? (
												<Text>{intl.formatMessage(answers.rewarding)}</Text>
											) : (
												<JsmPlaygroundRedactedLine width={220} />
											)}
										</SurveyCell>
										<SurveyCell>
											{answers ? (
												<Text color="color.text.subtle">
													{intl.formatDate(answers.submitted, {
														dateStyle: 'medium',
														timeStyle: 'short',
														timeZone: 'UTC',
													})}
												</Text>
											) : (
												<JsmPlaygroundRedactedLine width={104} />
											)}
										</SurveyCell>
									</SurveyTableRow>
								);
							})}
						</SurveyTableBody>
					</SurveyTable>
				</div>
				<JsmPlaygroundSidePanel isOpen={isPanelOpen} testId="jsm-hr-survey-response-track">
					<div id={panelId} css={panelFillStyles}>
						{panelResponse?.answers && (
							<SurveyResponsePanel
								key={panelResponse.id}
								response={{ ...panelResponse, answers: panelResponse.answers }}
								onClose={closePanel}
							/>
						)}
					</div>
				</JsmPlaygroundSidePanel>
			</div>
		</div>
	);
};

// ADS pads every tab panel by 8px inline, so this is the header's 32px less that.
const responsesStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.200'),
	boxSizing: 'border-box',
	width: '100%',
	paddingBlockStart: token('space.300'),
	paddingInline: token('space.400'),
});

const headingRowStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: token('space.200'),
});

const headingStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.100'),
});

const toolbarStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.100'),
});

const searchFieldStyles = css({
	display: 'flex',
	width: '192px',
});

// Sized by the table, not the stage, so the panel beside it starts and ends level with it.
const regionStyles = css({
	display: 'flex',
	flexShrink: 0,
});

const tableColumnStyles = css({
	flex: '1 1 auto',
	minWidth: 0,
});

const respondentCellStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: token('space.100'),
});

const personStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	minWidth: 0,
});

const nameStyles = css({
	minWidth: 0,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

const panelFillStyles = css({
	display: 'flex',
	width: '100%',
	height: '100%',
});
